import { Component, Injector, Input, OnInit } from '@angular/core';
import { BaseModalComponent, EnumValues, EnumObjectExtended } from '@card-software/library';
import { ApiResponse } from '@card-software/nexus-utilities';
import { FormControl, FormGroup, Validators, FormArray, FormBuilder } from '@angular/forms';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { MessageService } from 'primeng/api';
import { FilamentProperties, StapleProperties, Yarn, YarnComposition, YarnFormGroup, YarnType } from '../../../models/yarns/yarn';
import { Composition } from '../../../models/composition/composition';
import { ProductDevelopmentStatus, getValidStatusOptions } from '../../../models/enums/product-development-status';
import { CompositionProxyService } from '../../../proxies/item/composition-proxy.service';
import { EnumService } from '../../../services/enum.service';
import { MasterDataActionUtil } from '../../../utilities/master-data-action-util';
import { MasterData } from '@card-software/nexus-utilities';
import { GlobalMessageUtil, getLocalizedValue } from '@card-software/nexus-utilities';
import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { EditMasterdataDialogComponent } from '../../dialogs/edit-masterdata-dialog/edit-masterdata-dialog.component';
import { FormControlInvalidFieldsUtil } from '../../../utilities/form-control-invalid-fields.util';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { catchError, finalize, map, Observable, of } from 'rxjs';
import { LocaleHelperService } from '../../../services/localehelperservice.service';

@Component({
  selector: 'app-yarn-details-modal',
  templateUrl: './yarn-details-modal.component.html',
  styleUrl: './yarn-details-modal.component.scss',
})
export class YarnDetailsModalComponent extends BaseModalComponent<Yarn | null> implements OnInit {
  @Input() yarn: Yarn | null = null;
  @Input() canEdit = false;
  ref: DynamicDialogRef | undefined;
  compositionsFormArray: FormArray;
  yarnTypeEnums: EnumValues;

  isLoading = 0;
  filteredCompositions: Composition[] = [];
  formGroup: FormGroup<YarnFormGroup>;
  isEditing = false;
  availableCompositions: Composition[] = [];
  formGroupSubmitted = false;
  translations: Record<string, Translation> = {};

  statuses: EnumValues = {};
  statusesEnum?: EnumObjectExtended;
  status: FormControl<number> = new FormControl<number>(ProductDevelopmentStatus.Draft, { nonNullable: true });
  constructor(
    injector: Injector,
    private readonly yarnProxyService: YarnProxyService,
    private readonly compositionProxyService: CompositionProxyService,
    private readonly messageService: MessageService,
    private readonly enumService: EnumService,
    private fb: FormBuilder,
    private readonly translateService: TranslateService,
    private dialogService: DialogService,
    private localeHelperService: LocaleHelperService,
  ) {
    super(injector);
    this.yarnTypeEnums = this.enumService.getEnum('YarnType', 'itemEnums').values;
    this.statusesEnum = this.enumService.getEnum('ProductDevelopmentStatus', 'itemEnums');
    this.statuses = this.statusesEnum.values;
    this.compositionsFormArray = this.fb.array([], [Validators.required]);
    this.formGroup = this.fb.group({
      yarnCount: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
      yarnType: this.fb.control<number>(YarnType.Staple, [Validators.required]),
      stapleProperties: this.fb.group({
        ply: this.fb.control<number | null>(null, [Validators.required, Validators.min(0)]),
        fiberProcessingMethodCode: this.fb.control<string | null>(null, [Validators.required]),
        stapleYarnProcessingMethodCode: this.fb.control<string | null>(null, [Validators.required]),
        yarnEffectCode: this.fb.control<string | null>(null, []),
        yarnEndUseCode: this.fb.control<string | null>(null),
      }),
      filamentProperties: this.fb.group({
        filamentYarnProcessingMethodCode: this.fb.control<string | null>(null, []),
        numberOfFilaments: this.fb.control<number | null>(null, [Validators.required]),
        yarnLusterCode: this.fb.control<string | null>(null, []),
        yarnInterlacingLevelCode: this.fb.control<string | null>(null, []),
      }),
      compositions: this.compositionsFormArray,
    });
    this.formGroup.controls.filamentProperties.disable();
    this.formGroup.controls.stapleProperties.enable();

    this.formGroup.controls.yarnType.valueChanges.subscribe((value) => {
      if (value === YarnType.Filament) {
        this.formGroup.controls.filamentProperties.enable();
        this.formGroup.controls.stapleProperties.disable();
      } else {
        this.formGroup.controls.filamentProperties.disable();
        this.formGroup.controls.stapleProperties.enable();
      }
    });
    this.translations = {
      Error_Percentage_Sum: {
        es: 'Las composiciones deben sumar 100%. Total actual: {{total}}',
        en: 'Composition percentages must sum to exactly 100%. Current total: {{total}}',
      },
      Error_Add_Composition: {
        es: 'Debe agregar al menos una composición',
        en: 'At least one composition must be added',
      },
      Error_Form_Invalid: {
        es: 'El formulario tiene campos inválidos. Por favor, complete todos los campos requeridos',
        en: 'Form has invalid fields. Please fill all required fields',
      },
    };
  }

  get isReadonly(): boolean {
    return !this.canEdit || this.yarn?.status === ProductDevelopmentStatus.ReadyForProduction;
  }

  get yarnStatusOptions(): EnumValues {
    if (!this.canEdit) return {};
    const savedStatus = this.yarn?.status ?? ProductDevelopmentStatus.Draft;
    return getValidStatusOptions(savedStatus, this.statuses);
  }

  get isStaple(): boolean {
    return this.formGroup.controls.yarnType.value === YarnType.Staple;
  }

  get staplePropertiesFormGroup() {
    return this.formGroup.controls.stapleProperties;
  }

  get filamentPropertiesFormGroup() {
    return this.formGroup.controls.filamentProperties;
  }

  get yarnDescriptionPreview(): string {
    let description = '';
    if (this.isStaple) {
      description = `${this.formGroup.value?.yarnCount || '[blank]'}/${this.staplePropertiesFormGroup.value.ply || '[blank]'} ${
        this.staplePropertiesFormGroup.value.fiberProcessingMethodCode || '[blank]'
      } ${
        this.staplePropertiesFormGroup.value.stapleYarnProcessingMethodCode || '[blank]'
      }  ${this.staplePropertiesFormGroup.value.yarnEffectCode || ''}  `;
    } else {
      description = `${this.filamentPropertiesFormGroup.value.filamentYarnProcessingMethodCode || ''}
        ${this.formGroup.value?.yarnCount || '[blank]'}/${
          this.filamentPropertiesFormGroup.value.numberOfFilaments || '[blank]'
        } ${this.filamentPropertiesFormGroup.value.yarnLusterCode || ''} ${
          this.filamentPropertiesFormGroup.value.yarnInterlacingLevelCode || ''
        }`;
    }

    // Add composition with percentage and name
    const compositions = this.compositionsFormArray.controls
      .map((control) => {
        const percentage = control.get('percentage')?.value;
        let code = control.get('compositionCode')?.value;
        let name = '';
        if (typeof code === 'object' && code) {
          name = this.localeHelperService.getLocalizedValue(code.name);
          code = code.code;
        } else {
          name = this.getCompositionName(code);
        }
        return percentage && name ? `${percentage}% ${name}` : '';
      })
      .filter((str) => !!str)
      .join(', ');

    let result = description + (compositions ? `${compositions}` : '');
    result += ` ${this.staplePropertiesFormGroup.value.yarnEndUseCode || ''}`;
    return result.toUpperCase();
  }

  ngOnInit(): void {
    this.loadCompositions().subscribe((success) => {
      if (success) {
        if (this.yarn) {
          this.isEditing = true;
          this.status.setValue(this.yarn.status ?? ProductDevelopmentStatus.Draft);
          this.formGroup.patchValue({
            yarnCount: this.yarn.yarnCount,
            yarnType: this.yarn.yarnType,
          });

          if (this.yarn.yarnType === YarnType.Staple) {
            this.formGroup.patchValue({
              stapleProperties: {
                ply: this.yarn.stapleProperties?.ply || null,
                fiberProcessingMethodCode: this.yarn.stapleProperties?.fiberProcessingMethodCode || null,
                stapleYarnProcessingMethodCode: this.yarn.stapleProperties?.stapleYarnProcessingMethodCode || null,
                yarnEffectCode: this.yarn.stapleProperties?.yarnEffectCode || null,
                yarnEndUseCode: this.yarn.stapleProperties?.yarnEndUseCode || null,
              },
            });
          } else {
            this.formGroup.patchValue({
              filamentProperties: {
                filamentYarnProcessingMethodCode: this.yarn.filamentProperties?.filamentYarnProcessingMethodCode || null,
                numberOfFilaments: this.yarn.filamentProperties?.numberOfFilaments || null,
                yarnLusterCode: this.yarn.filamentProperties?.yarnLusterCode || null,
                yarnInterlacingLevelCode: this.yarn.filamentProperties?.yarnInterlacingLevelCode || null,
              },
            });
          }

          this.compositionsFormArray.clear();
          if (this.yarn.yarnComposition && this.yarn.yarnComposition.length > 0) {
            this.yarn.yarnComposition.forEach((comp, index) => {
              const compositionGroup = this.createCompositionFormGroup(comp);
              this.compositionsFormArray.push(compositionGroup);

              const codeControl = compositionGroup.get('compositionCode');
              if (codeControl) {
                codeControl.valueChanges.subscribe((code) => {
                  if (code) {
                    this.validateDuplicateComposition(code, index);
                  }
                });
              }

              const percentageControl = compositionGroup.get('percentage');
              if (percentageControl) {
                percentageControl.valueChanges.subscribe(() => {
                  this.validateCompositionTotal();
                });
              }
            });
          }
        }
        //Disable form by Rol or ReadyForProduction status
        if (!this.canEdit || this.yarn?.status === ProductDevelopmentStatus.ReadyForProduction) {
          this.formGroup.disable();
        }
      } else {
        // reload page after the 6 seconds
        setTimeout(() => {
          window.location.reload();
        }, 6000);
      }
    });
    // Only populate the values if we're editing an existing yarn
  }

  //#region Initialization
  loadCompositions(): Observable<boolean> {
    this.isLoading++;
    return this.compositionProxyService.getAllCompositions().pipe(
      map((compositions) => {
        this.availableCompositions = compositions.data?.filter((c) => c.isActive) || [];
        return true;
      }),
      catchError((error) => {
        console.error('Error loading compositions', error);
        return of(false);
      }),
      finalize(() => {
        this.isLoading--;
      }),
    );
  }

  //#endregion

  //#region Composition Management

  createCompositionFormGroup(composition?: YarnComposition): FormGroup {
    return this.fb.group({
      compositionCode: [
        this.availableCompositions
          .map((composition) => {
            return {
              ...composition,
              optionLabel: this.localeHelperService.getLocalizedValue(composition.name),
            };
          })
          .find((c) => c.code === composition?.compositionCode) || null,
        Validators.required,
      ],
      percentage: [composition?.percentage || 0, [Validators.required, Validators.min(1), Validators.max(100)]],
    });
  }

  addComposition(): void {
    if (this.validateCompositionTotal()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translateService.instant('Yarn_Details_Composition_Msg_PercentageError'),
        key: 'br',
        life: 2500,
      });
      return;
    }

    const newComposition = this.createCompositionFormGroup();

    const newCodeControl = newComposition.get('compositionCode');
    const newIndex = this.compositionsFormArray.length;

    if (newCodeControl) {
      newCodeControl.valueChanges.subscribe((code) => {
        if (code) {
          this.validateDuplicateComposition(code, newIndex);
        }
      });
    }

    const percentageControl = newComposition.get('percentage');
    if (percentageControl) {
      percentageControl.valueChanges.subscribe(() => {
        this.validateCompositionTotal();
      });
    }

    this.compositionsFormArray.push(newComposition);
  }

  removeComposition(index: number): void {
    this.compositionsFormArray.removeAt(index);
  }

  validateCompositionTotal(): boolean {
    if (this.compositionsFormArray.controls.length === 0) {
      return false;
    }

    const total = this.compositionsFormArray.controls.reduce((sum, control) => {
      return sum + (control.get('percentage')?.value || 0);
    }, 0);

    return total === 100;
  }

  getCompositionTotal(): number {
    return this.compositionsFormArray.controls.reduce((sum, control) => {
      return sum + (control.get('percentage')?.value || 0);
    }, 0);
  }

  isInvalidPercentage(): boolean {
    return this.compositionsFormArray.controls.some((control) => {
      const percentageValue = control.get('percentage')?.value;
      return percentageValue === 0;
    });
  }

  filterCompositions(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    this.filteredCompositions = this.availableCompositions
      .filter(
        (comp) =>
          this.localeHelperService.getLocalizedValue(comp.name).toLowerCase().includes(query) || comp.code?.toLowerCase().includes(query),
      )
      .map((composition) => {
        return {
          ...composition,
          optionLabel: this.localeHelperService.getLocalizedValue(composition.name),
        };
      });
  }

  validateDuplicateComposition(code: string, currentIndex: number): void {
    if (typeof code === 'object' && code) {
      const compositionCode = (code as Composition).code;

      const duplicateFound = this.compositionsFormArray.controls.some((control, index) => {
        const controlValue = control.get('compositionCode')?.value;
        const controlCode = typeof controlValue === 'object' ? (controlValue as Composition).code : controlValue;

        return index !== currentIndex && controlCode === compositionCode;
      });

      if (duplicateFound) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('Yarn_Details_Composition_Msg_CompositionDuplicate'),
          detail: `The composition "${this.localeHelperService.getLocalizedValue((code as Composition).name)}" is already added.`,
          key: 'br',
          life: 2500,
        });

        const control = this.compositionsFormArray.at(currentIndex);
        control.get('compositionCode')?.setValue(null);
      }
    }
  }

  //#endregion

  onClose() {
    this.close({ action: 'close', data: null });
  }

  handleDelete(event: { item: MasterData; confirmCallback: () => void; cancelCallback: () => void }) {
    MasterDataActionUtil.attemptToDeleteMasterData(event.item).subscribe({
      next: (confirmed) => {
        if (confirmed) {
          event.confirmCallback();
        } else {
          event.cancelCallback();
        }
      },
    });
  }

  //#region Edit MasterData

  handleUpdate(event: { item: MasterData; confirmCallback: (item: MasterData) => void; cancelCallback: () => void }) {
    this.ref = this.dialogService.open(EditMasterdataDialogComponent, {
      header: `Edit ${event.item.name}`,
      modal: true,
      closable: true,
      width: '400px',
      height: 'auto',
      data: {
        item: event.item,
        confirmCallback: event.confirmCallback,
        cancelCallback: event.cancelCallback,
      },
    });

    this.ref.onClose.subscribe((result: MasterData | null) => {
      if (result) {
        event.confirmCallback?.(result);
      } else {
        event.cancelCallback?.();
      }
    });
  }

  showMessagesApi(event: ApiResponse<MasterData>) {
    if (event.success) {
      GlobalMessageUtil.success(
        `${this.translateService.instant('Common_Success')}`,
        `${this.translateService.instant('Yarn_Details_Composition_Msg_RecordsDeleted')}`,
      );
    } else {
      GlobalMessageUtil.error(
        'Error',
        this.localeHelperService.getLocalizedValue(event.errorMessage) || `${this.translateService.instant('Fabric_Message_API_Error')}`,
      );
    }
  }

  //#endregion

  onSave() {
    this.formGroupSubmitted = true;
    this.formGroup.markAllAsTouched(); // Mark all controls as touched to trigger validation messages
    // Validate compositions are added
    if (this.compositionsFormArray.controls.length === 0) {
      GlobalMessageUtil.error(
        this.translateService.instant('Yarn_Details_Composition'),
        this.translations['Error_Add_Composition'][this.translateService.currentLang as keyof Translation],
      );
      return;
    }

    // Validate total percentage is exactly 100%
    if (!this.validateCompositionTotal() && !this.isInvalidPercentage()) {
      const total = this.getCompositionTotal();
      GlobalMessageUtil.error(
        this.translateService.instant('Yarn_Details_Modal_CompositionPercentage'),
        this.translations['Error_Percentage_Sum'][this.translateService.currentLang as keyof Translation].replace(
          '{{total}}',
          total.toString(),
        ),
      );
      return;
    }

    if (this.formGroup.invalid) {
      console.log('YarnDetailsModalComponent.onSave() - Form is invalid', FormControlInvalidFieldsUtil.getInvalidControls(this.formGroup));
      GlobalMessageUtil.error(
        this.translateService.instant('Form_Validation'),
        this.translations['Error_Form_Invalid'][this.translateService.currentLang as keyof Translation],
      );
      return;
    }

    if (this.formGroup.controls.yarnType.value === YarnType.Filament) {
      // check that filament form is valid
      if (!this.formGroup.controls.filamentProperties.valid) {
        GlobalMessageUtil.error(
          this.translateService.instant('Yarn_Details_Modal_FilamentProperties'),
          this.translateService.instant('Yarn_Details_Modal_Msg_FilamentPropertiesRequired'),
        );
        return;
      }
    } else {
      if (!this.formGroup.controls.stapleProperties.valid) {
        GlobalMessageUtil.error(
          this.translateService.instant('Yarn_Details_Modal_StapleProperties'),
          this.translateService.instant('Yarn_Details_Modal_Msg_StaplePropertiesRequired'),
        );
        return;
      }
    }

    const yarnComposition: YarnComposition[] = this.compositionsFormArray.controls.map((control) => {
      let compositionCode = control.get('compositionCode')?.value;

      if (typeof compositionCode === 'object' && compositionCode) {
        compositionCode = compositionCode.code;
      }

      return {
        compositionCode: compositionCode,
        percentage: control.get('percentage')?.value,
      };
    });

    //we already validated that yarnType is not null
    const stapleProperties: StapleProperties | undefined =
      this.formGroup.value.yarnType === YarnType.Staple
        ? {
            ply: this.formGroup.value.stapleProperties!.ply!,
            fiberProcessingMethodCode: this.formGroup.value.stapleProperties!.fiberProcessingMethodCode!,
            stapleYarnProcessingMethodCode: this.formGroup.value.stapleProperties!.stapleYarnProcessingMethodCode!,
            yarnEffectCode: this.formGroup.value.stapleProperties!.yarnEffectCode ?? undefined,
            yarnEndUseCode: this.formGroup.value.stapleProperties!.yarnEndUseCode || undefined,
          }
        : undefined;

    const filamentProperties: FilamentProperties | undefined =
      this.formGroup.value.yarnType === YarnType.Filament
        ? {
            filamentYarnProcessingMethodCode: this.formGroup.value.filamentProperties!.filamentYarnProcessingMethodCode ?? undefined,
            numberOfFilaments: this.formGroup.value.filamentProperties!.numberOfFilaments!,
            yarnLusterCode: this.formGroup.value.filamentProperties!.yarnLusterCode ?? undefined,
            yarnInterlacingLevelCode: this.formGroup.value.filamentProperties!.yarnInterlacingLevelCode ?? undefined,
          }
        : undefined;

    const yarn: Yarn = {
      id: this.yarn?.id || undefined,
      code: this.yarn?.code || undefined,
      yarnCount: this.formGroup.value.yarnCount!,
      yarnComposition: yarnComposition,
      yarnType: this.formGroup.value.yarnType!,
      longDescription: '', //API will set this based on properties
      status: this.status.value,
      stapleProperties: stapleProperties,
      filamentProperties: filamentProperties,
    };

    const method = this.yarn?.id ? this.yarnProxyService.updateYarn(yarn) : this.yarnProxyService.createYarn(yarn);

    this.isLoading++;
    method.subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.success) {
          GlobalMessageUtil.success(
            this.translateService.instant('Yarn_Details_Modal_Msg_YarnSave').replace('{{name}}', this.yarn?.code || ''),
            ` ${this.yarn ? this.translateService.instant('Yarn_Details_Modal_Msg_YarnUpdated').replace('{{name}}', this.yarn.code) : this.translateService.instant('Yarn_Details_Modal_Msg_YarnSaved').replace('{{name}}', response.data?.code || '')} .`,
          );
          this.close({ action: 'save', data: response.data });
        } else {
          GlobalMessageUtil.error(
            this.translateService.instant('Yarn_Details_Modal_Msg_YarnSaveError'),
            getLocalizedValue(this.translateService, response.errorMessage) ||
              this.translateService.instant('Yarn_Details_Modal_Msg_YarnWhileSaveError'),
          );
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  getCompositionName(code: string): string {
    const composition = this.availableCompositions.find((c) => c.code === code);
    return composition ? this.localeHelperService.getLocalizedValue(composition.name) : code;
  }

  public _setOptionsForPrimeNg(value: EnumValues) {
    return Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
  }
}
